import { LiveStatus, VideoLayout } from '@core/types';
import { FullScreenButton } from '@core/ui';
import {
    onDemandCurrentVideoTimeSeeded,
    onDemandCurrentVideoTimeUpdated,
    onDemandSetActiveSubtitleTrackId,
    useAppDispatch,
    useAppSelector
} from '@online-webinar/store';
import { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFullscreenController } from '../../../hooks/useFullscreenController';
import { markCompleted, writeResume } from '../../../utils/onDemandResume';
import { SubtitleDisplay } from '../../atoms/SubtitleDisplay';
import { SubtitlesMenu } from '../../atoms/SubtitlesMenu';
import { AppreciationPopup } from '../AppreciationPopup';

const HlsPlayer = lazy(() => import('@online-webinar/hls'));

/**
 * A seek is a command, not a state: "go to `time` now". The viewer can move playback themselves at
 * any moment (scrubber, keyboard) without that ever reaching us, so the same `time` can legitimately
 * be asked for twice in a row — clicking the same chapter again, re-opening the same `#<chapter>`
 * deep-link. `token` is what makes each of those a distinct request; it must change per request.
 */
export interface SeekRequest {
    time: number;
    token: number;
}

interface Props {
    webinarId: string;
    /** Scopes the local resume fallback per-viewer; undefined for anonymous viewers — see onDemandResume's key(). */
    participantId?: string;
    seek: SeekRequest;
    startPosition?: number;
    isLandscape: boolean;
    isMobile: boolean;
    videoLayout: number;
    videoUrl: string;
    onEnded?: () => void;
}

export const VideoOnDemand = ({ webinarId, participantId, seek, startPosition, isLandscape, isMobile, videoLayout, videoUrl, onEnded }: Props) => {
    const ref = useRef<HTMLVideoElement>(null);
    const videoContainerRef = useRef<HTMLDivElement>(null);
    const dispatch = useAppDispatch();
    const [trackIds, setTrackIds] = useState<string[]>([]);
    const subtitles = useAppSelector((state) => state.onDemandWebinar.subtitles);
    const activeSubtitleTrackId = useAppSelector((state) => state.onDemandWebinar.activeSubtitleTrackId);
    const activeSubtitles = useAppSelector((state) => state.onDemandWebinar.activeSubtitles) ?? [];
    const dataConnectionStatus = useAppSelector((state) => state.onDemandWebinar.dataConnection.status);
    const [isEnded, setIsEnded] = useState<boolean>(false);
    const [isPaused, setIsPaused] = useState<boolean>(false);
    const [shouldResume, setShouldResume] = useState<boolean>(false);
    const { t } = useTranslation();
    const { toggleFullscreen, isFauxFullscreen, isFullscreen } = useFullscreenController(videoContainerRef);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const hlsConfig = useMemo(() => (startPosition != null ? { startPosition } : undefined), []); // capture once, at mount
    const [mediaReady, setMediaReady] = useState(false);
    const lastResumeWriteRef = useRef(0);
    const seekEffectPrimed = useRef(false);

    // Pause the video if the data connection is not live, to prevent it from not properly registering time online
    // Resume the video when the connection goes back live, but only if it was playing before
    useEffect(() => {
        if (dataConnectionStatus === LiveStatus.Live) {
            if (shouldResume) {
                ref.current?.play().catch(() => {
                    /* Just ignore */
                });
                setShouldResume(false);
            }
            return;
        }

        if (!isPaused) {
            ref.current?.pause();
            setShouldResume(true);
        }
    }, [dataConnectionStatus, isPaused, shouldResume]);

    useEffect(() => {
        if (subtitles.length > 0) {
            setTrackIds(subtitles.map((track) => track.trackId));
        } else {
            setTrackIds([]);
        }
    }, [subtitles]);

    const getLayoutClasses = () => {
        let layoutSpecific = '';

        const mobileClasses = isMobile && isLandscape ? 'z-0' : '';

        if (!isFauxFullscreen && !isMobile) {
            switch (videoLayout) {
                case VideoLayout.VideoOnly:
                case VideoLayout.PresentationWithVideoLarge:
                case VideoLayout.DesktopSharing:
                    layoutSpecific = 'row-end-3 max-h-[calc(100dvh-var(--viewer-info-space))] bg-black';
                    break;
                case VideoLayout.PresentationWithVideoSmall:
                    layoutSpecific = 'w-1/4 min-w-48 self-end justify-self-end aspect-video m-4 z-1';
                    break;
                default:
                    layoutSpecific = 'max-h-[calc(50dvh-calc(var(--viewer-info-space)/2))]';
            }
        }

        return [layoutSpecific, mobileClasses].filter(Boolean).join(' ');
    };

    // Seeking only works once the media element knows its duration. Before that — e.g. an initial
    // deep-link seek fired before the lazy HLS player has attached and parsed the manifest — setting
    // currentTime is silently dropped. So apply immediately if metadata is ready, otherwise remember
    // the target and apply it on 'loadedmetadata' (see onLoadedMetadata on the player below).
    const pendingSeekRef = useRef<number | null>(null);

    useEffect(() => {
        // The organism seeds `seek` to the resume start position; applying it here would fight the
        // hls.js startPosition and flash frame 0. Skip the first run ONLY for that resume seed —
        // a URL-hash deep-link (#t= / #<chapter>) leaves startPosition undefined and must still seek.
        if (!seekEffectPrimed.current) {
            seekEffectPrimed.current = true;
            if (startPosition != null) return;
        }
        const video = ref.current;
        if (video && video.readyState >= HTMLMediaElement.HAVE_METADATA) {
            video.currentTime = seek.time;
            pendingSeekRef.current = null;
        } else {
            pendingSeekRef.current = seek.time;
        }
        // Keyed on the request token, not the position: the same position asked for twice is two
        // separate requests and both must seek.
    }, [seek.token]);

    const applyPendingSeek = () => {
        if (pendingSeekRef.current !== null && ref.current) {
            ref.current.currentTime = pendingSeekRef.current;
            pendingSeekRef.current = null;
        }
    };

    // Guards against the video having already ended: markCompleted() (see ended() below) already wrote
    // completed: true, and this must not race it back to false — a pagehide/visibilitychange right
    // after finishing would otherwise clobber that record with the final position at completed: false,
    // which the chapter-snap logic in computeResumeStart can then resolve to the last chapter's start.
    const writeResumeNow = () => {
        if (ref.current && !ref.current.ended && ref.current.currentTime > 0)
            writeResume(webinarId, ref.current.currentTime, false, participantId);
    };

    const onTimeUpdate = () => {
        if (ref.current && !ref.current.paused && !ref.current.seeking) {
            dispatch(onDemandCurrentVideoTimeUpdated(ref.current.currentTime));
            if (Date.now() - lastResumeWriteRef.current >= 5000) {
                lastResumeWriteRef.current = Date.now();
                writeResume(webinarId, ref.current.currentTime, false, participantId);
            }
        }
    };

    // onTimeUpdate only reports while playing, so a scrub performed while paused never reaches the
    // store and `activeChapter` goes stale — the chapter list highlights the wrong chapter, and the
    // hash deep-link guard refuses a jump to a chapter the viewer has actually already left.
    // Seeded, not updated: the saga forwards only onDemandCurrentVideoTimeUpdated to the server, and
    // a paused scrub should fix the UI without rewriting the stored resume position.
    const onSeeked = () => {
        if (ref.current) dispatch(onDemandCurrentVideoTimeSeeded(ref.current.currentTime));
    };

    useEffect(() => {
        const onHide = () => writeResumeNow();
        const onVisibility = () => {
            if (document.visibilityState === 'hidden') writeResumeNow();
        };
        window.addEventListener('pagehide', onHide);
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            window.removeEventListener('pagehide', onHide);
            document.removeEventListener('visibilitychange', onVisibility);
        };
        // writeResumeNow reads live refs; stable enough to bind once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [webinarId, participantId]);

    const ended = () => {
        if (ref.current) {
            dispatch(onDemandCurrentVideoTimeUpdated(ref.current.currentTime));
        }
        setIsEnded(true);
        markCompleted(webinarId, participantId);
        if (onEnded) {
            onEnded();
        }
    };

    const handleSubtitleMenuClick = (trackId: string) => {
        dispatch(onDemandSetActiveSubtitleTrackId(trackId));
    };

    const replay = () => {
        setIsEnded(false);
        if (ref.current) {
            ref.current.currentTime = 0;
            ref.current.play().catch(() => {
                /* Just ignore */
            });
        }
    };

    return (
        <>
            <div className={`col-start-1 row-start-1 ${getLayoutClasses()} min-h-0`}>
                <svg
                    width="1920"
                    height="1080"
                    viewBox="0 0 1920 1080"
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-auto max-w-full ${isMobile ? (isLandscape ? 'max-h-full' : 'max-h-96') : 'max-h-full'}`}
                    fill="transparent"
                >
                    <rect width="1920" height="1080" />
                </svg>
            </div>
            <div
                id="video"
                ref={videoContainerRef}
                className={`${isFauxFullscreen ? 'fixed inset-0 bg-black z-50' : 'relative'} col-start-1 row-start-1 ${getLayoutClasses()} flex overflow-hidden`}
            >
                <div
                    className={`relative max-w-full min-w-0 min-h-0 aspect-video ${isMobile && !isFauxFullscreen ? (isLandscape ? 'w-full max-h-full' : 'h-full max-h-96') : 'h-full max-h-full'} flex m-auto`}
                >
                    <div className="@container/video group/video relative w-full">
                        <svg
                            width="1920"
                            height="1080"
                            viewBox="0 0 1920 1080"
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-full w-full max-w-full max-h-full"
                        >
                            <rect width="1920" height="1080" fill="black" />
                        </svg>
                        {isEnded && <AppreciationPopup isOnDemand={true} onClick={replay} />}
                        {!mediaReady && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-white text-sm z-10 pointer-events-none">
                                {startPosition != null ? t('Player.Resume.Notice') : t('Player.Resume.Loading')}
                            </div>
                        )}
                        {/* No fallback here: the !mediaReady overlay above already covers this whole window (it only
                            clears on onLoadedData, strictly later than the lazy chunk itself finishing loading) — a
                            second overlay here would stack on top of it, both absolute inset-0 at the same z-index. */}
                        <Suspense fallback={null}>
                            <HlsPlayer
                                ref={ref}
                                autoPlay
                                playsInline
                                src={videoUrl}
                                controls
                                config={hlsConfig}
                                className="absolute inset-0 w-full h-full object-contain [&::-webkit-media-controls-fullscreen-button]:hidden"
                                onEnded={ended}
                                onPlay={() => setIsPaused(false)}
                                onPause={() => {
                                    setIsPaused(true);
                                    writeResumeNow();
                                }}
                                onTimeUpdate={onTimeUpdate}
                                onSeeked={onSeeked}
                                onLoadedMetadata={applyPendingSeek}
                                onLoadedData={() => setMediaReady(true)}
                                webinarId={webinarId}
                            />
                        </Suspense>
                        <div className="absolute top-2 right-2 z-1">
                            <SubtitlesMenu trackIds={trackIds} activeSubtitleTrackId={activeSubtitleTrackId} onSelect={handleSubtitleMenuClick} />
                            <FullScreenButton onClick={toggleFullscreen} isMaximized={isFauxFullscreen || isFullscreen} />
                        </div>
                        {activeSubtitles.length > 0 && <SubtitleDisplay activeSubtitles={activeSubtitles} isMobile={isMobile} isPaused={isPaused} />}
                    </div>
                </div>
            </div>
        </>
    );
};
